/**
 * Who sees which menu entries.
 *
 * The customer portal is shared between feed customers and UFC staff, so the
 * menu is not the same for everyone. Filtering here — rather than letting the
 * API refuse — is what stops a customer clicking Vehicles and getting a red
 * "Forbidden" where a page should be.
 *
 * This is presentation only. It hides links; it does not protect data. The
 * role's Strapi permissions are the boundary that matters, and both must agree:
 * a link shown to a role that cannot read it is a broken screen, and a role
 * that can read something it should not is a leak.
 */

import type { MenuAudience, MenuGroup, MenuViewer } from './types'

export function canSee(audience: MenuAudience | undefined, viewer: MenuViewer): boolean {
  if (!audience) return true
  if (audience === 'admin') return viewer.isAdmin
  return viewer.isStaff
}

/**
 * Drops entries the viewer should not see, then drops any group left with no
 * items — an empty expandable group is a dead control that looks like a bug.
 */
export function visibleMenuGroups(groups: MenuGroup[], viewer: MenuViewer): MenuGroup[] {
  return groups
    .filter((group) => canSee(group.audience, viewer))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canSee(item.audience, viewer)),
    }))
    .filter((group) => group.items.length > 0)
}
